/**
 * TODO: DELETE THIS FILE
 * This is a legacy component that shows the old implementation before the refactoring.
 * It is kept temporarily to support the feature flag transition (refactoredJobDetails).
 * Once the feature flag is fully rolled out and tested, this file should be deleted.
 *
 * The new implementation is in:
 * - JobDetails.tsx (main component with tabs)
 * - RunHistory.tsx (run list table)
 * - TasksList.tsx (tasks table)
 */

import { useLocation, useParams } from "react-router-dom";
import { useAppState } from "@/modules/app/useAppContext";
import usePaginate from "@/components/paginate/usePaginate";
import { KeyboardEvent, useState, useEffect } from "react";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { PAGE_SIZE_OPTIONS } from "@/pages/query/constants";
import { GetSnowflakeJobRunItem, WorkflowType } from "@/pages/query/types";
import { useQuery } from "@tanstack/react-query";
import {
  getSnowflakeJobRuns,
  getSnowflakeJobRunsGraphData,
  getSnowflakeJobsSummary,
} from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { getWorkflowNavigationPrefix } from "@/pages/query/snowflake/utils";
import { formatBytes, formatCost, formatMilliseconds } from "@/helpers/utils";
import dayjs from "dayjs";
import { ExecutionStatusCount } from "@/pages/query/ExecutionStatusCount";
import { Button, Label, NativeSelect, Stack, Typography } from "@altimateai/lego";
import { Graph } from "@/pages/query/Graph";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { RelativeComponentLoader } from "@/components/loader";

export const RunHistoryLegacy = () => {
  const { workflowId } = useParams();
  const { currency } = useAppState();
  const { page, setPage, pageSize, updatePageSize } = usePaginate();
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { startDate, endDate, navigateTo } = useDateFromSearchParams();
  const [graphSize, setGraphSize] = useState(PAGE_SIZE_OPTIONS[0].value);
  const location = useLocation();

  // Determine workflow type from the current path
  const workflowType = location.pathname.includes("/tableau-jobs")
    ? WorkflowType.TABLEAU
    : WorkflowType.CUSTOM;

  const { data: summaryData, isLoading: isSummaryLoading } = useQuery({
    queryKey: ["snowflake_job", workflowId, startDate, endDate, workflowType],
    queryFn: () =>
      getSnowflakeJobsSummary(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        workflow_type: workflowType,
      }),
  });

  const { data: runsData, isLoading: isRunsLoading } = useQuery({
    queryKey: ["snowflake_jobs", "runs", workflowId, startDate, endDate, page, pageSize],
    queryFn: () =>
      getSnowflakeJobRuns(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        page: page + 1,
        size: pageSize,
      }),
    staleTime: 500,
    enabled: Boolean(workflowId),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (runsData) {
      setTotalItems(runsData?.total || 0);
      setTotalPages(runsData?.pages || 0);
    }
  }, [runsData]);

  const { data: graphData, isLoading: isGraphLoading } = useQuery({
    queryKey: ["snowflake_jobs_graph", workflowId, startDate, endDate],
    queryFn: () =>
      getSnowflakeJobRunsGraphData(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      }),
    staleTime: 500,
    enabled: Boolean(workflowId),
  });

  const setSelectedRun = (runId: string) => {
    navigateTo(
      `${getWorkflowNavigationPrefix(workflowType)}/${encodeURIComponent(workflowId!)}/${runId}`
    );
  };

  const handleRunKeyDown = (event: KeyboardEvent<HTMLDivElement>, runId: string) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setSelectedRun(runId);
    }
  };

  const tableHeaders = [
    {
      id: 1,
      label: "Run ID",
      uid: "run_id",
      render: (item: GetSnowflakeJobRunItem) => (
        <div
          className="al-text-brand al-break-all cursor-pointer"
          onClick={() => setSelectedRun(item.run_id)}
          onKeyDown={event => handleRunKeyDown(event, item.run_id)}
          role="button"
          tabIndex={0}
        >
          {item.run_id}
        </div>
      ),
    },
    {
      id: 2,
      label: "Run Cost",
      uid: "query_cost",
      render: (item: GetSnowflakeJobRunItem) => <div>{formatCost(item.query_cost, currency)}</div>,
    },
    {
      id: 3,
      label: "Bytes Scanned",
      uid: "bytes_scanned",
      render: (item: GetSnowflakeJobRunItem) => <div>{formatBytes(item.bytes_scanned)}</div>,
    },
    {
      id: 4,
      label: "Start Time",
      uid: "start_time",
      render: (item: GetSnowflakeJobRunItem) => (
        <div>{dayjs(item.start_time).format("YYYY-MM-DD hh:mm a")}</div>
      ),
    },
    {
      id: 5,
      label: "Execution Time",
      uid: "execution_time",
      render: (item: GetSnowflakeJobRunItem) => (
        <div>{formatMilliseconds(item.execution_time)}</div>
      ),
    },

    {
      id: 6,
      label: "Run status",
      uid: "task_status",
      render: (item: GetSnowflakeJobRunItem) => (
        <ExecutionStatusCount run_count={item.total_count} success_count={item.success_count} />
      ),
    },
  ];

  if (isSummaryLoading || !summaryData) {
    return <RelativeComponentLoader label="Loading..." />;
  }

  return (
    <Stack direction="column" className="gap-sm">
      <Stack className="al-items-center flex-wrap col-gap-xl">
        {[
          {
            value: formatMilliseconds(summaryData.execution_time / summaryData.runs),
            label: "Average runtime",
          },
          {
            value: formatMilliseconds(summaryData.percentile_90_runtime),
            label: "90th percentile runtime",
          },
          {
            value: formatMilliseconds(summaryData.max_runtime),
            label: "Max runtime",
          },
          {
            value: formatMilliseconds(summaryData.min_runtime),
            label: "Min runtime",
          },
          {
            value: summaryData.runs,
            label: "Total runs",
          },
          {
            value: (summaryData.success_runs / summaryData.runs).toFixed(2),
            label: "Success ratio",
          },
          {
            // @ts-ignore
            value: formatCost(summaryData.query_cost / summaryData.runs, currency),
            label: "Average cost",
          },
        ].map(item => (
          <Stack key={item.label} className="al-items-center gap-xs">
            <div className="al-text-light-gray">{item.label}:</div>
            <div className="fw-semibold">{item.value}</div>
          </Stack>
        ))}
      </Stack>
      <Stack direction="column" className={"al-p-4 al-mt-2 al-border al-rounded-md"}>
        <Stack align="center">
          <Typography variant={"section-heading"}>Runs Cost</Typography>
          <div className="spacer" />
          {(graphData?.length || 0) > Number(PAGE_SIZE_OPTIONS[0].value) && (
            <Stack direction={"row"} align={"center"} gap={"md"}>
              <Label className={"al-m-0"}>Show Runs:</Label>
              <NativeSelect
                className={"al-min-w-36"}
                variant={"outline"}
                options={PAGE_SIZE_OPTIONS}
                value={graphSize}
                onChange={setGraphSize}
              />
            </Stack>
          )}
        </Stack>
        <Graph
          isLoading={isGraphLoading}
          data={graphData?.slice(0, Number(graphSize))?.toReversed() ?? []}
          setSelectedRun={setSelectedRun}
          annotationEnabled
        />
      </Stack>
      <Stack direction="column" className={"al-mt-4"}>
        <Stack align="center">
          <Typography variant={"section-heading"}>Runs List</Typography>
          <div className="spacer" />
          <Button
            onClick={() =>
              navigateTo(
                `${getWorkflowNavigationPrefix(workflowType)}/${encodeURIComponent(workflowId!)}/compare`
              )
            }
          >
            Compare Runs
          </Button>
        </Stack>
        <DataTable
          isLoading={isRunsLoading}
          tableClass="mt-0"
          header={tableHeaders}
          data={{
            items: runsData?.items ?? [],
          }}
          idKey={(item: GetSnowflakeJobRunItem) => item.run_id}
          gridTemplateColumns="2fr 1fr 1fr 1.5fr 1fr 1fr"
        />
        <Paginate
          itemCount={totalItems}
          numPages={totalPages}
          page={page}
          pageSize={pageSize}
          onPageClick={setPage}
          onPageSizeChange={updatePageSize}
          showPageSize
        />
      </Stack>
    </Stack>
  );
};
